import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import { useGoalModal } from '../contexts/GoalModalContext';
import BottomNavigation from '@/components/ui/BottomNavigation';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';

import { Avatar, AvatarSelector } from '../components';
import { useAvatar } from '../contexts/AvatarContext.tsx';
import { getMe, getUserBadges } from '@/services/authService';
import { paymentServiceApi, bookServiceApi } from '@/services/api';
import { Medal, Flame, Star, Heart, Settings, X, ChevronLeft, Brain, Briefcase, Target, Lightbulb, FlaskConical, Globe, GraduationCap, BookOpen, Coins, MessageCircle, Feather, Users2 } from 'lucide-react';
import { useUserTopics } from '@/features/learning/hooks/useUserTopics';
import { addUserTopics, removeUserTopics } from '@/features/learning/services/topicService';
import SelectionCard from '@/components/ui/SelectionCard';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import ErrorDisplay from '@/components/ui/ErrorDisplay';
import HeroPromoBanner from '@/features/library/components/HeroPromoBanner';

interface ProfilePageProps {
  className?: string;
}

// Shimmer Loading Component for Profile Page
const ProfileShimmer: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pb-24" dir="rtl">
      <div className="max-w-md mx-auto">
        {/* Top Navigation Bar */}
        <header className="px-6 pt-6 pb-2 flex items-center justify-between">
          <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
        </header>

        {/* Profile Info Section Shimmer */}
        <div className="flex flex-col items-center px-6 mb-2 -mt-2">
          <div className="relative mb-3">
            <div className="p-1 bg-white rounded-full shadow-sm">
              <div className="w-32 h-32 rounded-full bg-gray-200 animate-pulse" />
            </div>
          </div>
          <div className="h-6 w-32 rounded bg-gray-200 animate-pulse mb-2" />
          <div className="h-4 w-24 rounded bg-gray-200 animate-pulse" />
        </div>

        {/* Stats Row Shimmer */}
        <div className="px-6 mb-6">
          <div className="grid grid-cols-3 gap-3">
            {/* Achievements Shimmer */}
            <div className="bg-white rounded-2xl p-4 flex flex-col items-center justify-center aspect-[4/5] shadow-sm border border-gray-100">
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse mb-2" />
              <div className="h-7 w-8 rounded bg-gray-200 animate-pulse mb-1" />
              <div className="h-3 w-14 rounded bg-gray-200 animate-pulse" />
            </div>
            {/* XP Shimmer */}
            <div className="bg-white rounded-2xl p-4 flex flex-col items-center justify-center aspect-[4/5] shadow-sm border border-gray-100">
              <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse mb-2" />
              <div className="h-7 w-12 rounded bg-gray-200 animate-pulse mb-1" />
              <div className="h-3 w-14 rounded bg-gray-200 animate-pulse" />
            </div>
            {/* Streak Shimmer */}
            <div className="bg-white rounded-2xl p-4 flex flex-col items-center justify-center aspect-[4/5] shadow-sm border border-gray-100">
              <div className="w-8 h-8 rounded bg-gray-200 animate-pulse mb-2" />
              <div className="h-7 w-8 rounded bg-gray-200 animate-pulse mb-1" />
              <div className="h-3 w-12 rounded bg-gray-200 animate-pulse" />
            </div>
          </div>
        </div>

        {/* Subscription Card Shimmer */}
        <div className="px-6 mb-4">
          <div className="w-full h-24 bg-gray-200 rounded-3xl animate-pulse" />
        </div>

        {/* Topics & Goals Shimmer */}
        <div className="px-6 space-y-3">
          {/* Topics Button Shimmer */}
          <div className="w-full flex items-center justify-between p-4 bg-white rounded-3xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
              <div>
                <div className="h-4 w-28 rounded bg-gray-200 animate-pulse mb-1" />
                <div className="h-3 w-20 rounded bg-gray-200 animate-pulse" />
              </div>
            </div>
            <div className="w-5 h-5 rounded bg-gray-200 animate-pulse" />
          </div>

          {/* Goals Box Shimmer */}
          <div className="w-full bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Daily Goal Shimmer */}
            <div className="w-full flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
                <div>
                  <div className="h-4 w-20 rounded bg-gray-200 animate-pulse mb-1" />
                  <div className="h-3 w-16 rounded bg-gray-200 animate-pulse" />
                </div>
              </div>
              <div className="w-5 h-5 rounded bg-gray-200 animate-pulse" />
            </div>
            <div className="h-px bg-gray-100 mx-4" />
            {/* Streak Goal Shimmer */}
            <div className="w-full flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
                <div>
                  <div className="h-4 w-20 rounded bg-gray-200 animate-pulse mb-1" />
                  <div className="h-3 w-12 rounded bg-gray-200 animate-pulse" />
                </div>
              </div>
              <div className="w-5 h-5 rounded bg-gray-200 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};

// Stats Card Shimmer
const StatsCardShimmer: React.FC = () => (
  <div className="relative min-w-0 min-h-0 w-full overflow-visible rounded-3xl px-4 pt-2 pb-5 sm:px-5 flex flex-col items-center justify-between aspect-square ring-1 ring-black/10 bg-gray-200" style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}>
    <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse mb-2" />
    <div className="h-7 w-10 rounded bg-gray-200 animate-pulse mb-1" />
    <div className="h-3 w-14 rounded bg-gray-200 animate-pulse" />
  </div>
);

// Subscription Card Shimmer
const SubscriptionShimmer: React.FC = () => (
  <div className="w-full h-24 bg-gray-200 rounded-3xl animate-pulse" />
);

// Button Row Shimmer
const ButtonRowShimmer: React.FC = () => (
  <div className="w-full flex items-center justify-between p-4 bg-white rounded-3xl shadow-sm border border-gray-100">
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
      <div>
        <div className="h-4 w-24 rounded bg-gray-200 animate-pulse mb-1" />
        <div className="h-3 w-16 rounded bg-gray-200 animate-pulse" />
      </div>
    </div>
    <div className="w-5 h-5 rounded bg-gray-200 animate-pulse" />
  </div>
);

const ProfilePage = ({ className }: ProfilePageProps) => {
  const { user, logout, updateUser } = useAuth(); // updateUser added if needed
  const { } = useAvatar();
  const navigate = useNavigate();
  const { 
    showDailyGoalModal, 
    showStreakGoalModal, 
    openDailyGoalModal, 
    closeDailyGoalModal, 
    openStreakGoalModal, 
    closeStreakGoalModal 
  } = useGoalModal();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [profile, setProfile] = useState<{ name?: string; username?: string; phone_number?: string; email?: string; xp?: number; total_xp?: number; daily_goal?: number; streak_goal?: number } | null>(null);
  const [badgesCount, setBadgesCount] = useState<number>(0);
  const [currentStreak, setCurrentStreak] = useState<number>(0);
  const [profileLoading, setProfileLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [streakLoading, setStreakLoading] = useState(true);

  // Subscription status state
  const [subLoading, setSubLoading] = useState<boolean>(true); // Start true
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    is_active: boolean;
    end_date?: string;
    start_date?: string;
    days_left?: number;
    message: string;
    plan_name?: string;
  } | null>(null);

  // Topics State
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState<number[]>([]);
  const [tempSelectedTopicIds, setTempSelectedTopicIds] = useState<number[]>([]);
  const [topicsSaveSuccess, setTopicsSaveSuccess] = useState('');
  const [topicsSaveError, setTopicsSaveError] = useState('');
  const [isSavingTopics, setIsSavingTopics] = useState(false);

  // Goal display values (read-only for display)
  const [dailyGoal, setDailyGoal] = useState<number>(15);
  const [streakGoal, setStreakGoal] = useState<number>(30);

  const { userTopics, loading: topicsLoading, error: topicsError, refetch: refetchTopics } = useUserTopics();

  // Localize digits to Persian for numeric stats
  const toPersianDigits = (num: number | string) => String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

  const formatPersianDate = (dateStr?: string) => {
    if (!dateStr) return '';
    // Assumes format YYYY/MM/DD e.g. 1404/12/16
    const parts = dateStr.split('/');
    if (parts.length !== 3) return dateStr;
    const day = parts[2];
    const month = parts[1];
    const year = parts[0];

    const months = [
      'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
      'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
    ];
    const monthName = months[parseInt(month, 10) - 1] || month;

    return `${toPersianDigits(day)} ${monthName} ${toPersianDigits(year)}`;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [me, badges] = await Promise.all([
          getMe(),
          getUserBadges(),
        ]);
        if (mounted) {
          setProfile(me as any);
          setProfileLoading(false);
          const count = (badges as any)?.data?.user_badge_count;
          setBadgesCount(typeof count === 'number' ? count : 0);
          setStatsLoading(false);
          // Set daily_goal and streak_goal from API
          const meData = me as any;
          if (typeof meData?.daily_goal === 'number') {
            setDailyGoal(meData.daily_goal);
          }
          if (typeof meData?.streak_goal === 'number') {
            setStreakGoal(meData.streak_goal);
          }
        }
      } catch (e) {
        console.warn('⚠️ [Profile] Failed to load profile data', e);
        if (mounted) {
          setProfileLoading(false);
          setStatsLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Listen for goal updates
  useEffect(() => {
    const handleGoalUpdate = async () => {
      try {
        const me = await getMe();
        const meData = me as any;
        if (typeof meData?.daily_goal === 'number') {
          setDailyGoal(meData.daily_goal);
        }
        if (typeof meData?.streak_goal === 'number') {
          setStreakGoal(meData.streak_goal);
        }
      } catch (e) {
        console.warn('⚠️ [Profile] Failed to reload goals', e);
      }
    };

    window.addEventListener('goalUpdated', handleGoalUpdate);
    return () => window.removeEventListener('goalUpdated', handleGoalUpdate);
  }, []);

  // Load current_streak
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await bookServiceApi.get<{ current_streak?: number }>('/completed-sections/dates');
        if (mounted && res.success && res.data) {
          setCurrentStreak(typeof res.data.current_streak === 'number' ? res.data.current_streak : 0);
        }
      } catch {
        if (mounted) setCurrentStreak(0);
      } finally {
        if (mounted) setStreakLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Fetch subscription status
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setSubLoading(true);
        const res = await paymentServiceApi.get<typeof subscriptionStatus>(`/subscriptions/status`);
        if (!res.success) throw new Error(res.error || 'خطای دریافت وضعیت اشتراک');
        if (mounted) setSubscriptionStatus(res.data);
      } catch (e) {
        // Error handling
      } finally {
        if (mounted) setSubLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleLogout = () => {
    if (!showLogoutConfirm) {
      setShowLogoutConfirm(true);
      return;
    }
    logout();
    navigate('/welcome');
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleAchievementsClick = () => {
    navigate('/achievements');
  };

  const handleReferralClick = () => {
    navigate('/referral');
  };

  const handleAvatarClick = () => {
    setShowAvatarSelector(true);
  };

  const handleCloseAvatarSelector = () => {
    setShowAvatarSelector(false);
  };

  const handleAvatarSaved = async () => {
    setShowAvatarSelector(false);
    try {
      const me = await getMe();
      setProfile(me as any);
    } catch (error) {
      console.warn('⚠️ [Profile] Failed to refetch profile', error);
    }
  };

  // Handle topics modal
  const isTopicSelected = (topic: { state?: unknown }) => topic?.state === true || topic?.state === 'selected';
  const handleTopicsClick = () => {
    if (!Array.isArray(userTopics)) {
      setSelectedTopicIds([]);
      setTempSelectedTopicIds([]);
      setShowTopicsModal(true);
      return;
    }
    const initialSelected = userTopics
      .filter(t => isTopicSelected(t))
      .map(topic => topic.topic_id);
    setSelectedTopicIds(initialSelected);
    setTempSelectedTopicIds([...initialSelected]);
    setShowTopicsModal(true);
  };

  const handleTopicToggle = (topicId: number) => {
    setTempSelectedTopicIds(prev => {
      const isSelected = prev.includes(topicId);
      return isSelected
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId];
    });
  };

  const handleTopicsSave = async () => {
    try {
      setIsSavingTopics(true);
      setTopicsSaveError('');
      setTopicsSaveSuccess('');

      const currentlySelected = Array.isArray(userTopics)
        ? userTopics
          .filter(t => isTopicSelected(t))
          .map(topic => topic.topic_id)
        : [];

      const toAdd = tempSelectedTopicIds.filter(id => !currentlySelected.includes(id));
      const toRemove = currentlySelected.filter(id => !tempSelectedTopicIds.includes(id));

      let hasChanges = false;

      if (toAdd.length > 0) {
        const addResult = await addUserTopics(toAdd);
        if (!addResult.success) throw new Error(addResult.message || 'Failed to add topics');
        hasChanges = true;
      }

      if (toRemove.length > 0) {
        const removeResult = await removeUserTopics(toRemove);
        if (!removeResult.success) throw new Error(removeResult.message || 'Failed to remove topics');
        hasChanges = true;
      }

      if (hasChanges) {
        await refetchTopics();
        setTopicsSaveSuccess('تغییرات با موفقیت انجام شد');
      }

      setSelectedTopicIds([...tempSelectedTopicIds]);
      setTimeout(() => {
        setShowTopicsModal(false);
        setTopicsSaveSuccess('');
      }, 1500);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'خطا در ذخیره موضوعات';
      setTopicsSaveError(errorMessage);
    } finally {
      setIsSavingTopics(false);
    }
  };

  const handleTopicsCancel = () => {
    setTempSelectedTopicIds([...selectedTopicIds]);
    setShowTopicsModal(false);
  };

  const getTopicsDisplayText = () => {
    if (!Array.isArray(userTopics)) return 'هیچ موضوعی انتخاب نشده';
    const selectedCount = userTopics.filter(topic => isTopicSelected(topic)).length;
    if (selectedCount === 0) return 'هیچ موضوعی انتخاب نشده';
    if (selectedCount === 1) {
      const selectedTopic = userTopics.find(topic => isTopicSelected(topic));
      return selectedTopic?.topic_name || 'موضوع انتخاب شده';
    }
    return `${toPersianDigits(selectedCount)} موضوع انتخاب شده`;
  };

  const subtitle = profile?.username ? `@${profile.username.replace('@', '')}` : (profile?.phone_number ? toPersianDigits(formatPhoneNumber(profile.phone_number)) : (profile?.email || ''));

  // Formatting phone helper
  function formatPhoneNumber(phone: string) {
    return phone.replace(/(\d{4})(\d{3})(\d{4})/, '$1 $2 $3');
  }

  const iconForTopic = (name: string) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('روان') || n.includes('psych')) return <Brain className="w-5 h-5" />;
    if (n.includes('کسب') || n.includes('بیز') || n.includes('business') || n.includes('مدیر') || n.includes('رهبری')) return <Briefcase className="w-5 h-5" />;
    if (n.includes('بازاری') || n.includes('مارکت') || n.includes('فروش') || n.includes('هدف')) return <Target className="w-5 h-5" />;
    if (n.includes('ایده') || n.includes('خلاق') || n.includes('نوآوری') || n.includes('innovation')) return <Lightbulb className="w-5 h-5" />;
    if (n.includes('علم') || n.includes('شیمی') || n.includes('فیزیک') || n.includes('science')) return <FlaskConical className="w-5 h-5" />;
    if (n.includes('سلامت') || n.includes('خانواده') || n.includes('روابط') || n.includes('health')) return <Heart className="w-5 h-5" />;
    if (n.includes('جهان') || n.includes('تاریخ') || n.includes('بین') || n.includes('world')) return <Globe className="w-5 h-5" />;
    if (n.includes('یادگیری') || n.includes('آموزش') || n.includes('تحصیل') || n.includes('education')) return <GraduationCap className="w-5 h-5" />;
    if (n.includes('مالی') || n.includes('اقتصاد') || n.includes('finance') || n.includes('money')) return <Coins className="w-5 h-5" />;
    if (n.includes('ارتباط') || n.includes('communication')) return <MessageCircle className="w-5 h-5" />;
    if (n.includes('داستان') || n.includes('رمان') || n.includes('fiction') || n.includes('novel')) return <Feather className="w-5 h-5" />;
    return <BookOpen className="w-5 h-5" />;
  };

  return (
    <div className={cn('min-h-screen bg-gray-50 pb-24', className)} dir="rtl">
      <div className="max-w-md mx-auto">
        {/* Top Navigation Bar */}
        <header className="px-6 pt-6 pb-2 flex items-center justify-between">
          <button
            onClick={handleSettingsClick}
            className="w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Settings className="w-6 h-6" />
          </button>
        </header>

        {/* Profile Info Section */}
        <div className="flex flex-col items-center px-6 mb-2 -mt-2">
          <div className="relative mb-3">
            <div className="p-1 bg-white rounded-full shadow-sm">
              <Avatar
                size="xl"
                onClick={handleAvatarClick}
                className="w-32 h-32 hover:shadow-md transition-shadow cursor-pointer"
              />
            </div>
            {/* Edit Icon */}
            <button
              onClick={handleAvatarClick}
              className="absolute bottom-2 right-2 w-9 h-9 bg-primary-500 rounded-full flex items-center justify-center text-white border-2 border-white shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          </div>

          {profileLoading ? (
            <>
              <div className="h-6 w-28 rounded bg-gray-200 animate-pulse mb-2" />
              <div className="h-4 w-36 rounded bg-gray-200 animate-pulse" />
            </>
          ) : (
            <>
              {profile?.name && (
                <h2 className="text-xl font-bold text-gray-900 font-persian mb-1">
                  {toPersianDigits(profile.name)}
                </h2>
              )}
              {subtitle && (
                <p className="text-sm text-gray-500 font-sans" dir="ltr">
                  {subtitle}
                </p>
              )}
            </>
          )}
        </div>

        {/* Stats Row */}
        <div className="px-6 mb-6">
          <div className="grid grid-cols-3 gap-3">
            {/* Achievements */}
            {statsLoading ? (
              <StatsCardShimmer />
            ) : (
              <div
                onClick={handleAchievementsClick}
                className="relative min-w-0 min-h-0 w-full overflow-visible rounded-3xl px-4 pt-2 pb-5 sm:px-5 flex flex-col items-center justify-between text-center aspect-square ring-1 ring-black/10 bg-[#FFFDFC] cursor-pointer hover:bg-gray-50 transition-colors"
                style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
              >
                <div className="mb-1 flex h-8 w-8 items-center justify-center">
                  <Medal className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 font-sans">
                  {toPersianDigits(badgesCount)}
                </div>
                <div className="text-xs text-gray-400 font-medium font-persian">
                  دستاوردها
                </div>
              </div>
            )}

            {/* XP */}
            {profileLoading ? (
              <StatsCardShimmer />
            ) : (
              <div className="relative min-w-0 min-h-0 w-full overflow-visible rounded-3xl px-4 pt-2 pb-5 sm:px-5 flex flex-col items-center justify-between text-center aspect-square ring-1 ring-black/10 bg-[#FFFDFC]" style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}>
                <div className="mb-1 flex h-8 w-8 items-center justify-center">
                  <Star className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 font-sans">
                  {toPersianDigits(profile?.xp ?? profile?.total_xp ?? 0)}
                </div>
                <div className="text-xs text-gray-400 font-medium font-persian">
                  امتیاز کل
                </div>
              </div>
            )}

            {/* Streak */}
            {streakLoading ? (
              <StatsCardShimmer />
            ) : (
              <div
                onClick={() => navigate('/learning/streak-calendar')}
                className="relative min-w-0 min-h-0 w-full overflow-visible rounded-3xl px-4 pt-2 pb-5 sm:px-5 flex flex-col items-center justify-between text-center aspect-square ring-1 ring-black/10 bg-[#FFFDFC] cursor-pointer hover:bg-gray-50 transition-colors"
                style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
              >
                <div className="mb-1 flex h-8 w-8 items-center justify-center">
                  <Flame className="h-8 w-8 text-orange-500" />
                </div>
                <div className="text-xl sm:text-2xl font-bold text-gray-900 font-sans">
                  {toPersianDigits(currentStreak)}
                </div>
                <div className="text-xs text-gray-400 font-medium font-persian">
                  پیوستگی
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Status - Compact */}
        <div className="px-6 mb-4">
          {subLoading ? (
            <SubscriptionShimmer />
          ) : subscriptionStatus?.is_active ? (
            <div className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-6 text-white shadow-lg flex items-center justify-between">
              <div>
                <h3 className="font-persian font-bold text-lg mb-1">{subscriptionStatus.plan_name || 'اشتراک ویژه'}</h3>
                <p className="font-persian text-sm text-white/80">
                  {`پایان اعتبار: ${formatPersianDate(subscriptionStatus.end_date)}`}
                </p>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-3xl font-bold font-sans">{toPersianDigits(subscriptionStatus.days_left ?? 0)}</span>
                <span className="text-xs font-persian text-white/80">روز باقیمانده</span>
              </div>
            </div>
          ) : (
            <HeroPromoBanner location="profile" />
          )}
        </div>

        {/* Favorite Topics Selection (Moved from Settings) */}
        <div className="px-6 space-y-3">
          <button
            onClick={handleTopicsClick}
            disabled={topicsLoading}
            className="w-full flex items-center justify-between p-4 bg-white rounded-3xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-500 group-hover:scale-110 transition-transform">
                <Heart className="w-5 h-5" />
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 font-persian text-sm">موضوعات مورد علاقه</div>
                {topicsLoading ? (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                ) : (
                  <div className="text-xs text-muted font-persian mt-0.5">{getTopicsDisplayText()}</div>
                )}
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-gray-300" />
          </button>

          {/* Goals Box - Daily Goal & Streak Goal */}
          <div className="w-full bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Daily Goal */}
            <button
              onClick={openDailyGoalModal}
              disabled={profileLoading}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform">
                  <Target className="w-5 h-5" />
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900 font-persian text-sm">هدف روزانه</div>
                  {profileLoading ? (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <div className="text-xs text-muted font-persian mt-0.5">{toPersianDigits(dailyGoal)} دقیقه</div>
                  )}
                </div>
              </div>
              <ChevronLeft className="w-5 h-5 text-gray-300" />
            </button>

            <div className="h-px bg-gray-100 mx-4" />

            {/* Streak Goal */}
            <button
              onClick={openStreakGoalModal}
              disabled={profileLoading}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-500 group-hover:scale-110 transition-transform">
                  <Flame className="w-5 h-5" />
                </div>
                <div className="text-right">
                  <div className="font-bold text-gray-900 font-persian text-sm">هدف پیوستگی</div>
                  {profileLoading ? (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <div className="text-xs text-muted font-persian mt-0.5">{toPersianDigits(streakGoal)} روز</div>
                  )}
                </div>
              </div>
              <ChevronLeft className="w-5 h-5 text-gray-300" />
            </button>
          </div>

          <button
            onClick={handleReferralClick}
            className="w-full flex items-center justify-between p-4 bg-white rounded-3xl shadow-sm border border-[#B9F1DD] hover:bg-[#E9FBF4] transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#E9FBF4] flex items-center justify-center text-[#35D6A0] group-hover:scale-110 transition-transform">
                <Users2 className="w-5 h-5" />
              </div>
              <div className="text-right">
                <div className="font-bold text-gray-900 font-persian text-sm">معرفی دوستان</div>
              </div>
            </div>
            <ChevronLeft className="w-5 h-5 text-gray-300" />
          </button>
        </div>

      </div>

      <AvatarSelector
        isOpen={showAvatarSelector}
        onClose={handleCloseAvatarSelector}
        onSave={handleAvatarSaved}
      />

      {/* Topics Modal */}
      {showTopicsModal && (
        <div>
        <div
          className="fixed inset-0 bg-black/50 z-[100]"
          onClick={handleTopicsCancel}
        />
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className={cn(
            'w-full max-w-md max-h-[85vh] bg-white rounded-2xl flex flex-col shadow-xl overflow-hidden'
          )} dir="rtl">
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0 flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-foreground font-persian">موضوعات مورد علاقه</h2>
                  <button onClick={handleTopicsCancel} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                <div className="overflow-y-auto p-4 max-h-[55vh]">
                  {topicsLoading ? (
                    <div className="flex justify-center py-12"><LoadingSpinner /></div>
                  ) : topicsError ? (
                    <ErrorDisplay message={topicsError} onRetry={refetchTopics} />
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {(Array.isArray(userTopics) ? userTopics : []).map((topic) => {
                        const isSelected = tempSelectedTopicIds.includes(topic.topic_id);
                        return (
                          <div
                            key={topic.topic_id}
                            onClick={() => handleTopicToggle(topic.topic_id)}
                            className={cn(
                              "flex flex-col items-center justify-center p-2 rounded-xl border-2 transition-all cursor-pointer",
                              isSelected
                                ? "border-primary-500 bg-primary-50 text-primary-700"
                                : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-300"
                            )}
                          >
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center mb-1 transition-colors",
                              isSelected ? "bg-white text-primary-600" : "bg-white text-gray-500"
                            )}>
                              {iconForTopic(topic.topic_name)}
                            </div>
                            <span className="font-persian font-semibold text-[10px] text-center leading-tight">
                              {topic.topic_name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="px-6 py-4 flex-shrink-0 bg-white border-t border-gray-100">
                  {topicsSaveSuccess && <div className="mb-2 p-2 bg-green-100 border border-green-400 text-green-700 rounded-lg text-center font-persian text-xs">{topicsSaveSuccess}</div>}
                  {topicsSaveError && <div className="mb-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded-lg text-center font-persian text-xs">{topicsSaveError}</div>}
                  <Button variant="primary" onClick={handleTopicsSave} disabled={isSavingTopics} className="w-full h-11 text-sm font-bold shadow-md">
                    {isSavingTopics ? 'در حال ذخیره...' : 'ثبت'}
                  </Button>
                </div>
          </div>
        </div>
        </div>
      )}

      <div hidden={!showLogoutConfirm}>
            <div className="fixed inset-0 bg-black bg-opacity-40 z-50" />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-xl p-6 shadow-xl max-w-sm w-full" dir="rtl">
              <p className="text-center mb-6 font-persian">آیا مطمئن هستید که می‌خواهید خارج شوید؟</p>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={cancelLogout} className="flex-1 font-persian">انصراف</Button>
                <Button variant="primary" onClick={handleLogout} className="flex-1 font-persian bg-red-500 hover:bg-red-600 text-white">خروج</Button>
              </div>
            </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default ProfilePage;
